/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extend From
import Service, { service } from '@ember/service';
// Ember Imports
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment/moment';
import config from 'linced-app/config/environment';
// Common Imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import { onAttachmentUpload } from '@linc-technologies/linced-common/utils/uppy';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
// App Imports
import { generateSuspensionPost } from 'linced-app/utils/incidents';
import { SuspensionCategory } from '@linc-technologies/linced-common/enums/suspensions';
import { tracked } from '@glimmer/tracking';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

export default class SuspensionsService extends Service {
  // Services
  @service
  behaviourDates;

  @service
  dates;

  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  @service
  uppy;

  // Options
  @optionAlias('nswDOE:delInfo:name')
  delName;

  @optionAlias('hero:behaviours:doeContact')
  doePhoneNumber;
  @tracked principalDetails = null;
  @tracked suspensionPage = null;
  @tracked suspensionLettersPage = null;
  @tracked mode = 'action';
  @tracked attachments = [];
  @tracked suspension;

  //  Parent function
  processSuspensionDocuments(suspension, props) {
    let suspensionPromises = [];

    if (this.suspensionPage) {
      suspensionPromises.push(
        this.processSuspensionPost(suspension, props).catch(() => {
          this.toaster.error('Error processing suspension post');
        }),
      );
    }

    if (this.mode !== 'extend') {
      /*if (this.suspensionLettersPage) {
        suspensionPromises.push(
          this.processLetters(suspension, letters)
            .then(() => {
              suspension.set('caregiverNotified', true);
            })
            .catch(() => {
              this.toaster.error('Error processing letters');
            })
        );
      }*/
    }

    /*if (!isEmpty(suspension.extensions)) {
      suspensionPromises.push(
        this.processExtensionLetters(suspension, letters)
          .then(() => {
            suspension.set('caregiverNotified', true);
          })
          .catch(() => {
            this.toaster.error('Error processing extension letters');
          })
      );
    }*/

    if (suspension.category > SuspensionCategory.FORMAL_CAUTION) {
      suspensionPromises.push(
        this.setAttendanceForSuspension(suspension).catch((err) => {
          console.error(err);
          this.toaster.error('Error setting suspension attendance');
        }),
      );
    }

    return suspensionPromises;
  }

  async sendSuspensionLetter(suspension, letters) {
    if (this.suspensionLettersPage) {
      await this.processLetters(suspension, letters)
        .then(() => {
          suspension.set('caregiverNotified', true);
        })
        .catch(() => {
          this.toaster.error('Error processing letters');
        });
    }
  }

  async sendExtensionLetter(suspension, letters) {
    if (!isEmpty(suspension.extensions)) {
      await this.processExtensionLetters(suspension, letters)
        .then(() => {
          suspension.set('caregiverNotified', true);
        })
        .catch(() => {
          this.toaster.error('Error processing extension letters');
        });
    }
  }

  processSuspensionPost(suspension, props) {
    let content = generateSuspensionPost(suspension, props);
    let subject = `${SuspensionCategory.getLabel(props.category)}: ${moment(
      props.startDate,
      'YYYY-MM-DD',
    ).format('Do MMM YYYY')} - ${moment(
      suspension.displayEndDate,
      'YYYY-MM-DD',
    ).format('Do MMM YYYY')}`;

    if (suspension.get('post.id')) {
      // Post already exists - we are editing
      // Set new content then save post and suspension together
      return suspension
        .get('post')
        .then((post) => {
          post.set('subject', subject);
          post.set('content', content);
          return post.save().catch(() => {
            this.toaster.error('Error saving suspension post');
          });
        })
        .catch(() => {
          this.toaster.error('Error fetching suspension post');
        });
    }
    // no existing post - generate a new one
    let postParams = {
      postType: PostType.POST,
      status: PostStatus.DRAFT,
      schoolId: this.get('session.school'),
      displayDate: suspension.get('startDate'),
      authorId: this.get('session.person'),
      personId: suspension.get('learnerId'),
      pageId: this.suspensionPage,
      content,
      subject,
      jurisdiction: Jurisdiction.AU_NSW,
      isSickbay: true, // todo: change isSickbay to isSpecialUse
    };

    return this.store
      .createRecord('posts/post', postParams)
      .save()
      .then((post) => {
        suspension.set('post', post);
      })
      .catch(() => {
        this.toaster.error('Error saving suspension post');
      });
  }

  // Collates the required letters and processes them using a helper function
  processLetters(suspension, letters) {
    return Promise.resolve()
      .then(() => suspension.get('letters'))
      .then((existingLetters) => {
        // If letters already exist, skip generation
        if (!isEmpty(existingLetters)) {
          return Promise.resolve();
        }
        // Create new letters only on first time
        return this.generateNewLetters(suspension, letters, null);
      });
  }

  // Same as above but for extension letters
  processExtensionLetters(suspension, letters) {
    let extensions = suspension.get('extensions');
    return Promise.resolve()
      .then(() => suspension.get('extensionLetters'))
      .then((existingLetters) => {
        return Promise.all(
          extensions.map((extension) => {
            // Check if extension letters already exist
            let extensionLetters = [];
            if (!isEmpty(extension.letterIds)) {
              extensionLetters = existingLetters.filter((letter) =>
                extension.letterIds.includes(letter.id),
              );
            }

            // If extension letters already exist, do nothing
            if (!isEmpty(extensionLetters)) {
              return Promise.resolve();
            }

            return this.generateNewLetters(suspension, letters, extension);
          }),
        );
      });
  }

  // Calls the processTemplate function to generate the content for a given letter
  // template and params for each caregiver
  generateLettersContent(letterTemplate, suspension, extension) {
    // resolve the async hasMany relationship upfront.
    const caregiver = resolved(suspension.caregivers)[0];

    if (isEmpty(caregiver)) {
      return [];
    }

    return [
      {
        value: processTemplate(letterTemplate, {
          school: suspension.get('school'),
          person: suspension.get('learner'),
          caregiver: caregiver,
          behaviour: {
            delName: this.delName,
            principal: this.principalDetails,
            nominatedSchoolPerson: suspension.get('nominatedContact'),
            doePhoneNumber: this.doePhoneNumber,
          },
          suspension,
          extension,
        }),
      },
    ];
  }

  // Creates a new post for the given letter content array
  // Passing an extension causes any generated letters to be treated
  // as extension letters
  generateNewLetters(suspension, letters, extension) {
    let letterPromises = [];

    letters.forEach((letter) => {
      let postParams = {
        postType: PostType.POST,
        status: PostStatus.DRAFT,
        schoolId: this.get('session.school'),
        displayDate: suspension.get('recordedDate'),
        authorId: this.get('session.person'),
        personId: suspension.get('learnerId'),
        pageId: this.suspensionLettersPage,
        content: letter.value,
        subject: `${suspension.get('learner.fullName')}, Notice of ${
          suspension.get('category') === SuspensionCategory.FORMAL_CAUTION
            ? 'Formal Caution'
            : isEmpty(extension)
              ? 'Suspension'
              : 'Extension of Suspension'
        }`,
        jurisdiction: Jurisdiction.AU_NSW,
        isSuspensionLetters: true,
      };

      // Create a promise for saving the post and push it to letterPromises array
      letterPromises.push(
        this.store
          .createRecord('posts/post', postParams)
          .save()
          .then(async (post) => {
            // resolve the async hasMany relationship upfront.
            // The letter lists are async hasManys: push onto the loaded
            // ManyArrays.
            if (!isEmpty(extension)) {
              if (isEmpty(extension.letterIds)) {
                extension.set('letterIds', []);
              }
              extension.letterIds.push(post.id);
              (await suspension.extensionLetters).push(post);
              return;
            }
            await this.attachMediaToPost(post, suspension);
            (await suspension.letters).push(post);
          })
          .catch(() => {
            this.toaster.error('Error saving letter, please try again');
          }),
      );
    });

    return Promise.all(letterPromises).catch(() => {
      this.toaster.error('Error saving letter posts, please try again');
    });
  }

  attachMediaToPost(post, suspension) {
    if (isEmpty(this.attachments)) {
      return Promise.resolve();
    }

    let mediaPromises = this.attachments.map((attachment) => {
      let record = this.store.createRecord('media', {
        post,
        fileName: attachment.record.fileName,
        fileSize: attachment.record.fileSize,
        image: attachment.record.image,
        mediaSource: attachment.record.mediaSource,
        mediaType: attachment.record.mediaType,
        mimeType: attachment.record.mimeType,
        sharedWith: attachment.record.sharedWith,
        storageKey: attachment.record.storageKey,
        school: this.get('session.school'),
        person: this.get('session.person'),
        video: attachment.record.video,
      });

      return record.save().catch(() => {
        this.toaster.error('Error saving attachments, please try again');
      });
    });

    return Promise.all(mediaPromises)
      .then((mediaRecords) => {
        post.set('media', mediaRecords);
        suspension.set('attachments', mediaRecords);
        return post.save();
      })
      .catch(() => {
        this.toaster.error('Error processing media, please try again');
      });
  }

  onUploadSuccess(file, upload, mediaSource) {
    let type = 0;
    let key = '';
    switch (mediaSource) {
      case MediaSource.INTERNAL:
        type = MediaType.getValue(file.type.split('/')[0]) || MediaType.FILE;
        key = upload.uploadURL.match(/files\/(.+)\+/)[1];
        break;
      case MediaSource.CLOUDFLARE:
        type = MediaType.IMAGE;
        key = upload.body.result.id;
        break;
    }

    let sharedWith = 2;

    let record = this.store.createRecord('media', {
      schoolId: this.get('session.school.id'),
      personId: this.get('session.person'),
      sharedWith,
      mediaType: type,
      mediaSource: mediaSource,
      mimeType: file.type,
      fileName: file.name,
      storageKey: key,
      fileSize: file.size,
    });

    this.attachments = [...this.attachments, { file, upload, record }];
  }

  clearAttendance(earlyReturnDate, endDate) {
    let startDate = moment(earlyReturnDate, 'YYYY-MM-DD')
      .add(1, 'day')
      .format('YYYY-MM-DD');

    this.store
      .query('attendance/record', {
        personId: this.suspension.get('learner.id'),
        dateFrom: startDate,
        dateTo: endDate,
      })
      .then((records) => {
        let promises = records.map((record) => {
          if (record.code === 'E') {
            return record.destroyRecord().catch(() => {
              this.toaster.error('Error clearing attendance data!');
            });
          }
        });

        return Promise.all(promises);
      });
  }

  findExistingRecord(records, dateId) {
    return records.find((record) => {
      return record.date_id === dateId;
    });
  }

  // Sets attendance for the suspension period
  setAttendanceForSuspension(suspension, endDate) {
    endDate = endDate || suspension.get('endDate');
    let earlyReturnDate = suspension.get('earlyReturnDate');
    let code = 'E'; // Code for suspended in NSW
    let session = this.session;
    let learner = suspension.get('learner');
    let startDate = suspension.get('startDate');

    let dates = this.behaviourDates.calculateSchoolDaysInRange(
      startDate,
      endDate,
      true,
    );

    if (!isEmpty(earlyReturnDate)) {
      this.clearAttendance(earlyReturnDate, endDate);
    }

    return this.store
      .query('attendance/record', {
        personId: learner.get('id'),
        dateFrom: startDate,
        dateTo: endDate,
      })
      .then((existingRecords) => {
        let newRecords = [];
        let updatedRecords = [];

        dates.forEach((date) => {
          let day = this.dates.dayForDate(date);

          if (isEmpty(day)) {
            return;
          }

          let heroDate = this.dates.getDate(date);
          let periodIds = day.get('periodIds');

          if (isEmpty(periodIds)) {
            return;
          }

          let period = this.store.peekRecord(
            'timetable/period',
            periodIds.objectAt(0),
          );

          let effectiveFrom = moment(
            `${date}T${period.get('startTime')}`,
            'YYYY-MM-DDTHH:mm',
          ).unix();

          let newRecord = {
            authorId: session.get('person.id'),
            code: code,
            date,
            dateId: heroDate.id,
            effectiveFrom,
            homeroomId: learner.get('homeroom.id'),
            notes: 'Suspended',
            personId: learner.get('id'),
            periodId: period.id,
            schoolId: session.get('activeSchool'),
            movements: [
              {
                time: effectiveFrom,
                code: code,
                authorId: session.get('person.id'),
              },
            ],
          };

          let existingRecord = this.findExistingRecord(
            existingRecords,
            heroDate.id,
          );

          if (existingRecord) {
            let record = newRecord;
            record.id = existingRecord.id;

            updatedRecords.push(record);
            return;
          }

          newRecords.push(newRecord);
        });

        let promises = [];

        if (newRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: newRecords }),
          };

          promises.push(
            this.fetch
              .post(
                `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulk`,
                options,
              )
              .catch((err) => {
                console.error(err);
              }),
          );
        }

        if (updatedRecords.length > 0) {
          let options = {
            data: JSON.stringify({ records: updatedRecords }),
          };

          promises.push(
            this.fetch
              .post(
                `${config.APP.apiHost}/attendance/${config.APP.apiPath}/records/:bulkupdate`,
                options,
              )
              .catch((err) => {
                console.error(err);
              }),
          );
        }

        return Promise.all(promises);
      });
  }

  // initialise sets the service up for a suspension dialog. It resolves once
  // the suspension's existing attachments have loaded.
  async initialise(
    mode,
    principal,
    suspension,
    suspensionPage,
    suspensionLettersPage,
  ) {
    // severe issues with suspension dates when setting suspensions across multiple
    // years unless we load the records in first
    this.dates.loadYear(moment().add(1, 'year').format('YYYY'), true);

    this.mode = mode;
    this.principalDetails = principal;
    this.suspension = suspension;
    this.suspensionPage = suspensionPage;
    this.suspensionLettersPage = suspensionLettersPage;

    // Point the shared uppy at this list straight away, so files added while
    // the existing ones load land here rather than in another flow.
    let call = (this._initialiseCall = {});
    this.attachments = [];
    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        2,
        this.get('session.school'),
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });

    // resolve the async hasMany relationship upfront.
    // attachments is an async hasMany: load the existing files, then put them
    // ahead of anything uploaded in the meantime.
    let existing = (await suspension.attachments).map((record) => ({ record }));
    if (this._initialiseCall !== call) {
      // initialise ran again (e.g. didReceiveAttrs) while this one waited;
      // only the newest call may fill the list.
      return;
    }
    this.attachments = [...existing, ...this.attachments];
  }

  // Attachments
  removeAttachment(attachment) {
    this.attachments = this.attachments.filter((a) => a !== attachment);
  }
}
